import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../App';

const PermissionContext = createContext(null);

// Page ID to route mapping
const PAGE_ROUTE_MAP = {
  'dashboard': ['/dashboard', '/app'],
  'threat_intel': ['/threat-intelligence', '/threats', '/threat-dashboard', '/exploit-intelligence'],
  'assessments': ['/assessment-center', '/assessment'],
  'vulnerabilities': ['/vulnerabilities', '/vulnerability-hub'],
  'assets': ['/assets', '/asset-management', '/asset-discovery'],
  'raptor': ['/raptor'],
  'easm': ['/easm'],
  'remediation_factory': ['/remediation-factory'],
  'security_initiatives': ['/security-initiatives'],
  'compliance': ['/compliance', '/compliance-reporting'],
  'reports': ['/reporting', '/reports'],
  'itsm': ['/itsm'],
  'identity_exposure': ['/identity-exposure'],
  'crown_jewels': ['/crown-jewels'],
  'threat_hunting': ['/threat-hunting-playbooks'],
  'ai_insights': ['/ai-insights', '/ai-remediation'],
  'administration': ['/administration', '/platform-admin', '/registrations', '/mfa-settings', '/notification-settings'],
};

// Default permissions by role
const DEFAULT_ROLE_PERMISSIONS = {
  platform_admin: {
    all_access: true,
  },
  admin: {
    dashboard: { view: true, write: true, edit: true, delete: true },
    threat_intel: { view: true, write: true, edit: true, delete: true },
    assessments: { view: true, write: true, edit: true, delete: true },
    vulnerabilities: { view: true, write: true, edit: true, delete: true },
    assets: { view: true, write: true, edit: true, delete: true },
    raptor: { view: true, write: true, edit: true, delete: true },
    easm: { view: true, write: true, edit: true, delete: true },
    remediation_factory: { view: true, write: true, edit: true, delete: true },
    security_initiatives: { view: true, write: true, edit: true, delete: true },
    compliance: { view: true, write: true, edit: true, delete: true },
    reports: { view: true, write: true, edit: true, delete: true },
    itsm: { view: true, write: true, edit: true, delete: true },
    identity_exposure: { view: true, write: true, edit: true, delete: true },
    crown_jewels: { view: true, write: true, edit: true, delete: true },
    threat_hunting: { view: true, write: true, edit: true, delete: true },
    ai_insights: { view: true, write: true, edit: true, delete: true },
    administration: { view: true, write: true, edit: true, delete: true },
  },
  analyst: {
    dashboard: { view: true, write: false, edit: false, delete: false },
    threat_intel: { view: true, write: true, edit: false, delete: false },
    assessments: { view: true, write: true, edit: false, delete: false },
    vulnerabilities: { view: true, write: true, edit: true, delete: false },
    assets: { view: true, write: false, edit: false, delete: false },
    raptor: { view: true, write: true, edit: false, delete: false },
    easm: { view: true, write: false, edit: false, delete: false },
    remediation_factory: { view: true, write: true, edit: true, delete: false },
    compliance: { view: true, write: false, edit: false, delete: false },
    reports: { view: true, write: true, edit: false, delete: false },
    ai_insights: { view: true, write: false, edit: false, delete: false },
  },
  viewer: {
    dashboard: { view: true, write: false, edit: false, delete: false },
    threat_intel: { view: true, write: false, edit: false, delete: false },
    vulnerabilities: { view: true, write: false, edit: false, delete: false },
    assets: { view: true, write: false, edit: false, delete: false },
    compliance: { view: true, write: false, edit: false, delete: false },
    reports: { view: true, write: false, edit: false, delete: false },
  },
};

export function PermissionProvider({ children, user }) {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      if (!user) {
        setPermissions(null);
        setLoading(false);
        return;
      }

      try {
        // Try to load custom role permissions from API
        const response = await api.get('/auth/my-permissions');
        if (response.data?.page_permissions) {
          setPermissions({
            role: user.role,
            page_permissions: response.data.page_permissions,
            custom_role: response.data.custom_role,
          });
        } else {
          // Fall back to default role permissions
          const rolePerms = DEFAULT_ROLE_PERMISSIONS[user.role] || DEFAULT_ROLE_PERMISSIONS.viewer;
          setPermissions({
            role: user.role,
            page_permissions: rolePerms,
            custom_role: null,
          });
        }
      } catch (error) {
        // Use default permissions on error
        const rolePerms = DEFAULT_ROLE_PERMISSIONS[user.role] || DEFAULT_ROLE_PERMISSIONS.viewer;
        setPermissions({
          role: user.role,
          page_permissions: rolePerms,
          custom_role: null,
        });
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [user]);

  const canAccessPage = (pageId) => {
    if (!permissions) return false;
    if (permissions.page_permissions?.all_access) return true;
    
    const pagePerms = permissions.page_permissions?.[pageId];
    return pagePerms?.view === true;
  };

  const canAccessRoute = (route) => {
    if (!permissions) return false;
    if (permissions.page_permissions?.all_access) return true;

    // Find which page this route belongs to
    for (const [pageId, routes] of Object.entries(PAGE_ROUTE_MAP)) {
      if (routes.some(r => route.startsWith(r))) {
        return canAccessPage(pageId);
      }
    }
    
    // Default allow if route not mapped
    return true;
  };

  const canWrite = (pageId) => {
    if (!permissions) return false;
    if (permissions.page_permissions?.all_access) return true;
    return permissions.page_permissions?.[pageId]?.write === true;
  };

  const canEdit = (pageId) => {
    if (!permissions) return false;
    if (permissions.page_permissions?.all_access) return true;
    return permissions.page_permissions?.[pageId]?.edit === true;
  };

  const canDelete = (pageId) => {
    if (!permissions) return false;
    if (permissions.page_permissions?.all_access) return true;
    return permissions.page_permissions?.[pageId]?.delete === true;
  };

  const value = {
    permissions,
    loading,
    canAccessPage,
    canAccessRoute,
    canWrite,
    canEdit,
    canDelete,
    isAdmin: user?.role === 'admin' || user?.role === 'platform_admin',
    isPlatformAdmin: user?.role === 'platform_admin',
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

export default PermissionContext;
